// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * RFC 9457 `application/problem+json` handling for browser clients.
 *
 * ADR-0028 makes Problem Details the project error contract. The UI parses the
 * envelope defensively (`docs/05-CODING-STANDARDS.md` §9: external input is
 * parsed with Zod) and converts it into one typed error class so feature code
 * branches on a stable `kind`, never on a message string.
 *
 * The UI classifies problems for presentation only. `CST-048` keeps the
 * authoritative authorization decision on the server: a `denied` classification
 * changes what the user is told, never what the user is allowed to do.
 */

import { z } from 'zod';

/** Media type mandated for the error envelope (ADR-0028). */
export const PROBLEM_JSON_MEDIA_TYPE = 'application/problem+json';

/**
 * Presentation classes for a failed request.
 *
 * `docs/10-TESTING.md` §30 requires distinct permission-denied, timeout,
 * offline, and degraded states; this union is what makes those states
 * exhaustively renderable.
 */
export type ProblemKind =
  | 'unauthenticated'
  | 'denied'
  | 'notFound'
  | 'conflict'
  | 'validation'
  | 'rateLimited'
  | 'timeout'
  | 'offline'
  | 'upstreamUnavailable'
  | 'server'
  | 'client';

/** Field-level problem extension emitted by the BFF for validation failures. */
export const fieldProblemSchema = z.object({
  pointer: z.string().max(256),
  message: z.string().max(512),
});

/** Field-level problem extension. */
export type FieldProblem = z.infer<typeof fieldProblemSchema>;

/**
 * RFC 9457 problem document.
 *
 * Unknown members are preserved by RFC 9457 but deliberately dropped here: the
 * UI must not surface undocumented server internals
 * (`API-012`, `07-SECURITY.md` §15.5).
 */
export const problemDetailsSchema = z.object({
  type: z.string().default('about:blank'),
  title: z.string().max(256),
  status: z.number().int().min(100).max(599),
  detail: z.string().max(2048).optional(),
  instance: z.string().max(2048).optional(),
  code: z.string().max(128).optional(),
  correlation_id: z.string().max(128).optional(),
  errors: z.array(fieldProblemSchema).max(50).optional(),
});

/** Parsed problem document. */
export type ProblemDetails = z.infer<typeof problemDetailsSchema>;

/** Fallback title used when the server response is not a valid problem document. */
const UNPARSEABLE_TITLE = 'Unexpected response';

/**
 * Map an HTTP status to a presentation class.
 *
 * @param status - HTTP status code.
 * @returns The presentation class for that status.
 */
export function classifyStatus(status: number): ProblemKind {
  switch (status) {
    case 401:
      return 'unauthenticated';
    case 403:
      return 'denied';
    case 404:
      return 'notFound';
    case 409:
    case 412:
      return 'conflict';
    case 400:
    case 422:
      return 'validation';
    case 429:
      return 'rateLimited';
    case 504:
      return 'timeout';
    case 502:
    case 503:
      return 'upstreamUnavailable';
    default:
      break;
  }
  if (status >= 500) {
    return 'server';
  }
  return 'client';
}

/** Options accepted by {@link ApiProblemError}. */
export interface ApiProblemErrorOptions {
  readonly kind: ProblemKind;
  readonly status: number;
  readonly problem?: ProblemDetails | undefined;
  readonly cause?: unknown;
}

/**
 * The single error type raised by every shared API client.
 *
 * Feature components render {@link ApiProblemError.kind}; they never inspect a
 * raw response or an unstructured message.
 */
export class ApiProblemError extends Error {
  /** Presentation class for this failure. */
  readonly kind: ProblemKind;

  /** HTTP status, or `0` when the request never reached the server. */
  readonly status: number;

  /** Parsed problem document when the server supplied one. */
  readonly problem: ProblemDetails | undefined;

  /**
   * @param message - Safe, user-presentable summary.
   * @param options - Classification and optional parsed problem document.
   */
  constructor(message: string, options: ApiProblemErrorOptions) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiProblemError';
    this.kind = options.kind;
    this.status = options.status;
    this.problem = options.problem;
  }

  /** Field-level problems, empty when the server reported none. */
  get fieldProblems(): readonly FieldProblem[] {
    return this.problem?.errors ?? [];
  }

  /** Correlation identifier operators can use, when the server supplied one. */
  get correlationId(): string | undefined {
    return this.problem?.correlation_id;
  }

  /**
   * Whether a client may safely retry the identical request.
   *
   * `RUN-010` forbids retrying an operation with an unknown outcome, so only
   * transport-level and explicitly transient classes are retryable here.
   */
  get retryable(): boolean {
    return (
      this.kind === 'timeout' ||
      this.kind === 'offline' ||
      this.kind === 'upstreamUnavailable' ||
      this.kind === 'rateLimited'
    );
  }
}

/**
 * Build an {@link ApiProblemError} from a failed HTTP response.
 *
 * @param response - The non-OK response.
 * @param body - Already-read response body, if any.
 * @returns A classified error.
 */
export function problemFromResponse(response: Response, body: unknown): ApiProblemError {
  const parsed = problemDetailsSchema.safeParse(body);
  const problem = parsed.success ? parsed.data : undefined;
  const kind = classifyStatus(response.status);
  const message = problem?.title ?? UNPARSEABLE_TITLE;
  return new ApiProblemError(message, { kind, status: response.status, problem });
}
